import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { FeatureFlagsModal } from '../Partials/FeatureFlagsModal';

interface FeatureFlagsModalControls {
  openFeatureFlagsModal: () => void;
  closeFeatureFlagsModal: () => void;
  isFeatureFlagsModalOpen: boolean;
}

const FeatureFlagsModalContext = createContext<FeatureFlagsModalControls | undefined>(undefined);

/**
 * Hook to open the super-admin feature flags modal from anywhere in the app.
 *
 * The modal is mounted once at the root, so any surface can raise it without
 * owning visibility state. Non-super-admins get a no-op: the modal renders
 * nothing for them, so callers never need to gate on the role themselves.
 *
 * @example
 * ```tsx
 * const { openFeatureFlagsModal } = useFeatureFlagsModal();
 * <button onClick={openFeatureFlagsModal}>Feature Flags</button>
 * ```
 */
export const useFeatureFlagsModal = (): FeatureFlagsModalControls => {
  const context = useContext(FeatureFlagsModalContext);
  if (!context) {
    throw new Error('useFeatureFlagsModal must be used within a FeatureFlagsModalProvider');
  }
  return context;
};

export const FeatureFlagsModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);

  const openFeatureFlagsModal = useCallback(() => setIsOpen(true), []);
  const closeFeatureFlagsModal = useCallback(() => setIsOpen(false), []);

  const controls = useMemo(
    () => ({ openFeatureFlagsModal, closeFeatureFlagsModal, isFeatureFlagsModalOpen: isOpen }),
    [openFeatureFlagsModal, closeFeatureFlagsModal, isOpen],
  );

  return (
    <FeatureFlagsModalContext.Provider value={controls}>
      {children}
      <FeatureFlagsModal visible={isOpen} onClose={closeFeatureFlagsModal} />
    </FeatureFlagsModalContext.Provider>
  );
};
